/** How far ahead the meeting calendars let a user look. */
export const BOOKING_WINDOW_YEARS = 1;

/**
 * The latest date any meeting calendar accepts. Without an upper bound the
 * Material year picker scrolls arbitrarily far ahead, so a student can land on
 * a date centuries out; nothing further than the booking window is ever
 * intentional.
 *
 * Computed per call rather than held as a module constant so a tab left open
 * overnight doesn't keep yesterday's window. End-of-day so the final day of
 * the window stays fully selectable.
 *
 * This is the real cutoff for what a user can select — pair it with a
 * dateFilter/validator wherever a calendar is bound. It is NOT what should be
 * fed to the picker's own `[max]`/`[maxDate]` input; see pickerMaxDate().
 * @returns {Date}
 */
export function latestBookableDate(): Date {
  const date = new Date();
  date.setFullYear(date.getFullYear() + BOOKING_WINDOW_YEARS);
  date.setHours(23, 59, 59, 999);
  return date;
}

/**
 * Angular Material's multi-year calendar view always renders a fixed-size
 * page of years (`yearsPerPage`, currently 24, internal to
 * @angular/material/datepicker). When a `max` is bound, Material anchors that
 * page so it ends on `max`'s year — if `max` is less than a full page ahead
 * of `min`, the page pads backward to fill it out, rendering years before
 * `min` as extra disabled cells (e.g. a 2026-2027 window renders as
 * 2004-2027, with 2004-2025 greyed out for no reason).
 *
 * This value exists purely to keep that page aligned to start at the current
 * year instead of padding backward — it is NOT the real booking cutoff.
 * Everywhere this is bound to a picker's `[max]`/`[maxDate]`, pair it with a
 * dateFilter/validator that enforces latestBookableDate() for actual
 * selectability.
 * @returns {Date}
 */
export function pickerMaxDate(): Date {
  const MATERIAL_MULTI_YEAR_PAGE_SIZE = 24;
  const date = new Date();
  date.setFullYear(date.getFullYear() + MATERIAL_MULTI_YEAR_PAGE_SIZE - 1);
  date.setHours(23, 59, 59, 999);
  return date;
}
